import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, Search, UserRound } from 'lucide-react';
import { formatDateWord, formatDateInputISO } from '@/lib/dateFormat';
import { formatStoreName } from '@/lib/storeUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getCustomersPaginated } from '@/lib/api';

const CustomerInfoCard = ({
  orderData,
  formData,
  onFormChange,
  editable = true,
  stores = [],
  currentStore,
  onStoreChange,
  onReassignCustomer,
  reassigningCustomer = false,
}) => {
  const [localNotes, setLocalNotes] = useState(formData.notes || '');
  const [changeClientOpen, setChangeClientOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Sync localNotes with formData when it changes externally
  useEffect(() => {
    setLocalNotes(formData.notes || '');
  }, [formData.notes]);

  useEffect(() => {
    if (!changeClientOpen) {
      setClientSearch('');
      setClientResults([]);
      setSelectedClient(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setClientLoading(true);
      try {
        const response = await getCustomersPaginated({
          page: 1,
          page_size: 30,
          search: clientSearch.trim() || undefined,
          sort_by: 'customer_no',
          sort_order: 'desc',
        });
        if (cancelled) return;
        const rows = response?.data?.data || [];
        setClientResults(rows.filter((customer) => customer.customer_id !== orderData.customer_id));
      } catch (error) {
        if (!cancelled) setClientResults([]);
      } finally {
        if (!cancelled) setClientLoading(false);
      }
    }, clientSearch.trim() ? 300 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [changeClientOpen, clientSearch, orderData.customer_id]);
  
  const formatDate = (dateString) => {
    return formatDateWord(dateString, '');
  };

  const getPickerDate = (dateString) => {
    if (!dateString) return undefined;
    const parsed = new Date(dateString);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const renderDatePicker = (field, value, testId, clearTestId, { allowClear = true } = {}) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-8 w-full justify-start text-xs font-normal"
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-3.5 w-3.5 text-slate-500" />
          {value ? formatDate(value) : 'Select date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={getPickerDate(value)}
          onSelect={(date) => onFormChange(field, date ? formatDateInputISO(date) : '')}
          initialFocus
        />
        {allowClear && (
        <div className="border-t p-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full text-xs"
            onClick={() => onFormChange(field, '')}
            data-testid={clearTestId}
          >
            Clear date
          </Button>
        </div>
        )}
      </PopoverContent>
    </Popover>
  );

  const handleNotesBlur = () => {
    if (localNotes !== formData.notes) {
      onFormChange('notes', localNotes);
    }
  };

  return (
    <Card className="bg-white shadow-sm" data-testid="edit-order-left-customer-info-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-900">Customer Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2" data-testid="edit-order-left-customer-compact-list">
          <div className="flex items-center justify-between gap-3">
            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Client No</label>
            <div className="text-xs font-mono font-medium text-slate-900 text-right" data-testid="edit-order-left-customer-no">
              {orderData.customer_no ?? '—'}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Name</label>
            <div className="text-xs font-medium text-slate-900 text-right break-all">{orderData.customer_name || '—'}</div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Email</label>
            <div className="text-xs text-slate-700 text-right break-all">{orderData.customer_email || '—'}</div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Phone</label>
            <div className="text-xs text-slate-700 text-right">{orderData.customer_phone || '—'}</div>
          </div>
          {editable && onReassignCustomer && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs"
              onClick={() => setChangeClientOpen(true)}
              data-testid="edit-order-change-client-button"
            >
              <UserRound className="w-3.5 h-3.5 mr-1" />
              Change client
            </Button>
          )}
          <div className="flex items-center justify-between gap-3">
            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Store</label>
            {editable && onStoreChange && stores.length > 0 ? (
              <Select
                value={currentStore ?? formData.store ?? orderData.store ?? '__none__'}
                onValueChange={onStoreChange}
              >
                <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="header-store-edit-select-trigger">
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="header-store-edit-option-none">Not specified</SelectItem>
                  {stores.map((store) => (
                    <SelectItem
                      key={store.store_id}
                      value={store.store_id}
                      data-testid={`header-store-edit-option-${store.store_id}`}
                    >
                      {store.name || store.store_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-xs text-slate-700 text-right">
                {(() => {
                  const storeValue = currentStore ?? formData.store ?? orderData.store;
                  if (!storeValue || storeValue === '__none__') return '—';
                  return formatStoreName(storeValue) || orderData.store_name || storeValue;
                })()}
              </div>
            )}
          </div>
        </div>

        <div className="pt-3 border-t border-slate-100" data-testid="edit-order-left-dates-section">
          <CardTitle className="text-sm font-semibold text-slate-900 mb-3">Order Dates</CardTitle>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Order Date</label>
              {editable ? (
                renderDatePicker(
                  'order_date',
                  formData.order_date,
                  'edit-order-left-order-date-input',
                  'edit-order-left-order-date-clear-button',
                  { allowClear: false }
                )
              ) : (
                <div className="text-xs text-slate-700">{formatDate(formData.order_date || orderData.order_date) || '—'}</div>
              )}
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Test Fit Date</label>
              {editable ? (
                renderDatePicker(
                  'test_fit_date',
                  formData.test_fit_date,
                  'edit-order-left-test-fit-date-input',
                  'edit-order-left-test-fit-date-clear-button'
                )
              ) : (
                <div className="text-xs text-slate-700">{formatDate(formData.test_fit_date) || '—'}</div>
              )}
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Internal Delivery Date</label>
              {editable ? (
                renderDatePicker(
                  'internal_delivery_date',
                  formData.internal_delivery_date,
                  'edit-order-left-internal-delivery-date-input',
                  'edit-order-left-internal-delivery-date-clear-button'
                )
              ) : (
                <div className="text-xs text-slate-700">{formatDate(formData.internal_delivery_date) || '—'}</div>
              )}
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Client Final Date</label>
              {editable ? (
                renderDatePicker(
                  'client_final_date',
                  formData.client_final_date,
                  'edit-order-left-client-final-date-input',
                  'edit-order-left-client-final-date-clear-button'
                )
              ) : (
                <div className="text-xs text-slate-700">{formatDate(formData.client_final_date) || '—'}</div>
              )}
            </div>
          </div>
        </div>

        <div className="pt-3 border-t border-slate-100" data-testid="edit-order-left-notes-section">
          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-2">Order Notes</label>
          {editable ? (
            <Textarea
              value={localNotes}
              onChange={(e) => setLocalNotes(e.target.value)}
              onBlur={handleNotesBlur}
              placeholder="Add any special instructions or notes..."
              className="min-h-[76px] text-xs"
              data-testid="edit-order-left-notes-textarea"
            />
          ) : (
            formData.notes && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
                <div className="text-xs text-amber-800">{formData.notes}</div>
              </div>
            )
          )}
        </div>
      </CardContent>
      <Dialog open={changeClientOpen} onOpenChange={setChangeClientOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-order-change-client-dialog">
          <DialogHeader>
            <DialogTitle>Change client on this order</DialogTitle>
            <DialogDescription>
              Payments, garments, and design details stay on this order. Client no, name, email, and phone will switch to the selected customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={clientSearch}
                onChange={(event) => setClientSearch(event.target.value)}
                placeholder="Search name, client no, email, or phone..."
                className="pl-9"
                data-testid="edit-order-change-client-search"
              />
            </div>
            <div className="max-h-[280px] overflow-y-auto space-y-2">
              {clientLoading ? (
                <p className="text-center text-sm text-muted-foreground py-8">Loading clients...</p>
              ) : clientResults.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-8">No other clients found</p>
              ) : (
                clientResults.map((customer) => {
                  const selected = selectedClient?.customer_id === customer.customer_id;
                  return (
                    <button
                      key={customer.customer_id}
                      type="button"
                      onClick={() => setSelectedClient(customer)}
                      className={`w-full p-3 rounded-md border text-left ${
                        selected ? 'bg-primary/10 border-primary/40' : 'bg-secondary/30 border-white/5 hover:border-primary/30'
                      }`}
                      data-testid={`edit-order-change-client-row-${customer.customer_id}`}
                    >
                      <p className="text-sm font-medium">
                        {customer.customer_no != null ? `#${customer.customer_no} · ` : ''}
                        {customer.customer_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {[customer.email, customer.phone_number].filter(Boolean).join(' · ') || 'No contact details'}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setChangeClientOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!selectedClient || reassigningCustomer}
              onClick={async () => {
                await onReassignCustomer(selectedClient);
                setChangeClientOpen(false);
              }}
              data-testid="edit-order-change-client-confirm"
            >
              {reassigningCustomer ? 'Updating...' : 'Assign to this client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CustomerInfoCard;